import {
  useEffect,
  useMemo,
  useRef,
  type PointerEvent as ReactPointerEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { experienceCategories } from '@/mocks/properties';

type Category = (typeof experienceCategories)[number];

function ExperienceCard({ c, className }: { c: Category; className: string }) {
  const hrefByCategory: Record<string, string> = {
    'gulf-front': '/gulf-front-search-results',
    pool: '/pool-search-results',
    'large-groups': '/large-groups-search-results',
    'outdoor-living': '/outdoor-living-search-results',
    'golf-cart': '/golf-cart-search-results',
    'pet-friendly': '/pets-search-results',
  };

  return (
    <a
      href={hrefByCategory[c.id] ?? '/vacation-search-results'}
      draggable={false}
      className={`group relative overflow-hidden rounded-2xl bg-background-200 cursor-pointer ${className}`}
    >
      <img
        src={c.image}
        alt={c.label}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1200ms] group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/80 via-foreground-950/15 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-6 md:p-7 text-background-50">
        <div className="flex items-end justify-between gap-4">
          <div>
            <h3 className="font-heading text-2xl md:text-4xl leading-tight">{c.label}</h3>
            <p className="mt-2 text-sm md:text-base text-background-100/85 font-light italic">
              {c.tagline}
            </p>
          </div>
          <span className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-full bg-background-50/15 backdrop-blur-sm border border-background-50/30">
            <i className="ri-arrow-right-up-line" />
          </span>
        </div>
      </div>
    </a>
  );
}

export default function Experiences() {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const paused = useRef(false);
  const drag = useRef({ down: false, startX: 0, startScroll: 0, moved: false });
  const loopedCategories = useMemo(
    () => [...experienceCategories, ...experienceCategories, ...experienceCategories],
    [],
  );

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const setMiddle = () => {
      el.scrollLeft = el.scrollWidth / 3;
    };
    const keepLooping = () => {
      const loopWidth = el.scrollWidth / 3;
      if (el.scrollLeft < loopWidth * 0.35) el.scrollLeft += loopWidth;
      if (el.scrollLeft > loopWidth * 1.65) el.scrollLeft -= loopWidth;
    };

    setMiddle();

    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) >= Math.abs(e.deltaX)) {
        el.scrollLeft += e.deltaY;
        keepLooping();
        e.preventDefault();
      }
    };
    const onScroll = () => keepLooping();

    let frame = 0;
    const drift = () => {
      if (!paused.current && !drag.current.down) {
        el.scrollLeft += 0.35;
        keepLooping();
      }
      frame = window.requestAnimationFrame(drift);
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('scroll', onScroll, { passive: true });
    frame = window.requestAnimationFrame(drift);

    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('scroll', onScroll);
      window.cancelAnimationFrame(frame);
    };
  }, []);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el) return;
    drag.current = { down: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false };
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || !drag.current.down) return;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 5) drag.current.moved = true;
    el.scrollLeft = drag.current.startScroll - dx;
  };
  const endDrag = () => {
    drag.current.down = false;
  };
  const pauseDrift = () => {
    paused.current = true;
  };
  const resumeDrift = () => {
    paused.current = false;
    endDrag();
  };
  const onClick = (e: ReactMouseEvent<HTMLAnchorElement>) => {
    if (drag.current.moved) e.preventDefault();
  };

  return (
    <section
      id="experiences"
      className="relative bg-background-100/50 py-20 md:py-32 overflow-hidden border-y border-background-200/70"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div className="max-w-2xl">
            <div className="text-[11px] uppercase tracking-[0.24em] text-accent-700 mb-4">
              <span className="inline-block w-6 h-px bg-accent-500 align-middle mr-3" />
              Browse by experience
            </div>
            <h2 className="font-heading text-foreground-950 text-4xl md:text-6xl leading-[1.05] tracking-[-0.01em]">
              Find your stay by
              <span className="block italic font-light text-foreground-600">what matters to you.</span>
            </h2>
          </div>
        </div>
      </div>

      {/* Desktop: user-controlled horizontal scroller */}
      <div
        ref={scrollRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerEnter={pauseDrift}
        onPointerLeave={resumeDrift}
        className="hidden lg:flex gap-6 overflow-x-auto overflow-y-hidden cursor-grab active:cursor-grabbing subtle-scroll px-5 md:px-10 pb-2 select-none"
      >
        {loopedCategories.map((c, index) => (
          <ExperienceCard
            key={`${c.id}-${index}`}
            c={c}
            className="w-[360px] xl:w-[420px] flex-shrink-0 aspect-[3/4]"
          />
        ))}
      </div>

      {/* Mobile: tall vertical panels */}
      <div className="lg:hidden px-5 md:px-10 flex flex-col gap-6">
        {experienceCategories.map((c) => (
          <ExperienceCard key={c.id} c={c} className="w-full aspect-[4/5]" />
        ))}
      </div>
    </section>
  );
}
